import { useCallback, useEffect, useState } from 'react';
import Lozenge from '@atlaskit/lozenge';
import Spinner from '@atlaskit/spinner';
import { useFlags } from '@atlaskit/flag';
import SharedButton from 'shared/components/buttons/button';
import { useDialog } from 'core/providers/DialogProvider';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import DeliveryRequestsApi, { IRequestDecision } from 'core/api/delivery-requests/delivery-requests-api.service';
import TransportGroupsApiService from 'core/api/transport-groups/transport-groups-api.service';
import {
  IEligibleDriver,
  ITransportGroupPreview,
} from 'core/api/transport-groups/transport-groups-api-interface';
import TransportRunMap from 'modules/transport/transport-groups/transport-run-map';
import DriverPicker, { ANY_DRIVER } from 'modules/transport/transport-groups/driver-picker';
import FixAddress from './fix-address';

/**
 * Committing the queue, and deciding what the accepted work becomes.
 *
 * Three steps, because there are three questions and they arrive in order:
 * individual jobs or one sequenced batch; does the route look right; and who is
 * taking it. Asking them on one screen would mean solving a route nobody wanted.
 *
 * All three are answered before anything happens. The driver is chosen last but
 * still before the write, so the decisions, the jobs, the batch and the handover
 * land together — committing first and asking afterwards emptied the queue
 * under somebody who was still looking at the question.
 *
 * Nothing is written until the final step. The route is solved from the requests, which
 * already carry resolved addresses, so closing this at any point before Save
 * leaves no jobs, no batch and no marks disturbed — the failure this replaced
 * was a "Build a run" button that created held jobs the moment it was pressed.
 *
 * The decisions are the ones the queue held when Commit was pressed. Anything
 * marked since is not in here: a route is agreed on what was on screen.
 */

interface IProps {
  decisions: IRequestDecision[];
  /** How many would become work, since only those can go in a batch. */
  accepting: number;
}

/**
 * Below this there is nothing to sequence.
 *
 * One job is collected in one place and delivered in another whatever order it
 * is put in, so offering to optimise it implies a choice that does not exist —
 * and a route drawn for it is a picture of the only thing it could ever do.
 */
const WORTH_SEQUENCING = 2;

type Step = 'choose' | 'preview' | 'assign';

const CommitDialog = ({ decisions, accepting }: IProps) => {
  const dialog = useDialog();
  const flags = useFlags();

  const [step, setStep] = useState<Step>('choose');
  const [busy, setBusy] = useState(false);
  const [solving, setSolving] = useState(false);
  const [preview, setPreview] = useState<ITransportGroupPreview>();
  const [problemError, setProblemError] = useState<string>();
  const [fixing, setFixing] = useState<string>();
  const [drivers, setDrivers] = useState<IEligibleDriver[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [driversError, setDriversError] = useState<string>();
  const [chosen, setChosen] = useState<string>(ANY_DRIVER);

  const acceptUids = decisions.filter((decision) => decision.decision === 'accept').map((decision) => decision.uid);

  /**
   * Solves what is in front of us, again.
   *
   * Re-run after every correction rather than patched: a fixed address changes
   * the order of the whole route, not just its own position in it.
   */
  const solve = useCallback(async () => {
    setSolving(true);
    setProblemError(undefined);
    try {
      const { preview: solved } = await TransportGroupsApiService.preview(acceptUids);
      setPreview(solved);
    } catch (caught) {
      // Kept in the dialog: "set where runs start first" and "none of these can
      // be routed" are both instructions, and both are acted on here.
      setProblemError((caught as Error).message);
      setPreview(undefined);
    } finally {
      setSolving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decisions]);

  /** Kept out of the effect so the retry button can call the same thing. */
  const loadDrivers = useCallback(() => {
    setLoadingDrivers(true);
    setDriversError(undefined);
    TransportGroupsApiService.drivers()
      .then(({ drivers: eligible }) => setDrivers(eligible))
      .catch((caught) => setDriversError((caught as Error).message))
      .finally(() => setLoadingDrivers(false));
  }, []);

  useEffect(() => {
    if (step !== 'assign') return;
    loadDrivers();
  }, [step, loadDrivers]);

  const commit = async (buildRun: boolean, giveTo?: IEligibleDriver) => {
    setBusy(true);
    try {
      const { results, group: built, groupError } = await DeliveryRequestsApi.commitDecisions(
        decisions,
        buildRun,
        giveTo ? { uid: giveTo.uid, fullName: giveTo.fullName } : undefined,
      );
      const failed = results.filter((result) => result.error);

      if (failed.length) {
        showErrorFlag(
          `${failed.length} of ${results.length} could not be done`,
          failed.map((result) => result.error).join(' '),
          flags,
        );
      }

      if (built) {
        showSuccessFlag(
          giveTo ? 'Batch assigned' : 'Batch created',
          giveTo
            ? `${built.reference} (${built.label}) has been assigned to ${giveTo.fullName}.`
            : `${built.reference} (${built.label}) is available for any driver to accept.`,
          flags,
        );
        dialog?.closeDialog();
        return;
      }

      // The decisions were carried out and the run was not. Saying so plainly
      // matters: the marks are spent either way, so anybody told this failed
      // would go looking for work that has already been booked.
      if (groupError) {
        showErrorFlag(
          'Committed, but not as a batch',
          `${groupError} Anything that booked is an ordinary job, in the drivers' pool.`,
          flags,
        );
        dialog?.closeDialog();
        return;
      }

      if (!failed.length) {
        showSuccessFlag('Done', `${results.length} request${results.length === 1 ? '' : 's'} updated.`, flags);
      }
      dialog?.closeDialog();
    } catch (caught) {
      showErrorFlag('Nothing was committed', (caught as Error).message, flags);
      setBusy(false);
    }
  };

  const takeOut = async (uid: string) => {
    try {
      await DeliveryRequestsApi.clearMark(uid);
      await solve();
    } catch (caught) {
      showErrorFlag('That could not be taken out', (caught as Error).message, flags);
    }
  };

  const title =
    step === 'choose'
      ? `Commit ${decisions.length} request${decisions.length === 1 ? '' : 's'}`
      : step === 'preview'
      ? 'Review the batch'
      : 'Assign this batch';

  return (
    <div className='flex w-full flex-col rounded-lg bg-white lg:w-[760px]' data-testid='commit-dialog'>
      <div className='border-b p-4'>
        <p className='headline-05 font-semibold'>{title}</p>
      </div>

      <div className='max-h-[60vh] overflow-y-auto p-4'>
        {step === 'choose' && (
          <>
            <p className='body-02'>
              {accepting === 0 &&
                'None of these become work for a driver, so there is nothing to sequence.'}
              {accepting === 1 &&
                'One of these becomes work for a driver. A single job has nothing to sequence, so it goes straight to them.'}
              {accepting >= WORTH_SEQUENCING &&
                `${accepting} of these will become work for a driver. The rest are forwarded or closed either way.`}
            </p>

            {accepting >= WORTH_SEQUENCING && (
              <div className='mt-4 flex flex-col gap-3'>
                {/* Disabled together while either is running: both write, and
                    a card that looks pressable after it has been pressed is how
                    the same six requests get committed twice. */}
                <button
                  type='button'
                  data-testid='choose-individual'
                  disabled={busy}
                  onClick={() => commit(false)}
                  className='rounded border p-4 text-left hover:border-blue-400 disabled:cursor-not-allowed disabled:opacity-60'
                >
                  <p className='body-02 flex items-center gap-2 font-semibold'>
                    Save as individual jobs
                    {busy && <Spinner size={16} />}
                  </p>
                  <p className='body-01 mt-1 text-gray-600'>
                    {busy
                      ? 'Committing…'
                      : 'Each request becomes its own job. Any driver can accept them individually.'}
                  </p>
                </button>

                <button
                  type='button'
                  data-testid='choose-optimise'
                  disabled={busy}
                  onClick={() => {
                    setStep('preview');
                    solve();
                  }}
                  className='rounded border p-4 text-left hover:border-blue-400 disabled:cursor-not-allowed disabled:opacity-60'
                >
                  <p className='body-02 font-semibold'>Combine into one batch</p>
                  <p className='body-01 mt-1 text-gray-600'>
                    Sequence the jobs into one batch for one driver, and look at the route first.
                  </p>
                </button>
              </div>
            )}
          </>
        )}

        {step === 'preview' && (
          <>
            {solving && (
              <div className='flex justify-center py-10' data-testid='solving'>
                <Spinner size={40} />
              </div>
            )}

            {!solving && problemError && (
              <p className='body-02 text-red-600' data-testid='preview-error'>
                {problemError}
              </p>
            )}

            {!solving && preview && (
              <>
                <TransportRunMap stops={preview.stops} />

                <div className='mt-3 flex border-b py-3 body-02'>
                  <p>
                    Stops: <span className='font-semibold'>{preview.stops.length}</span>
                  </p>
                  <p className='ml-2 border-l pl-2'>
                    Distance: <span className='font-semibold'>{preview.summary.mileage} miles</span>
                  </p>
                  <p className='ml-2 border-l pl-2'>
                    Driving: <span className='font-semibold'>{preview.summary.duration}</span>
                  </p>
                  <p className='ml-2 border-l pl-2'>
                    From: <span className='font-semibold'>{preview.startsFrom}</span>
                  </p>
                </div>

                <div className='mt-3' data-testid='preview-stops'>
                  {preview.stops.map((stop) => (
                    <div key={stop.sequence} className='flex gap-3 border-b py-3'>
                      <span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-800 text-xs font-semibold text-white'>
                        {stop.sequence}
                      </span>
                      <div>
                        <p className='body-02 font-semibold'>
                          {stop.site}
                          {stop.point ? ` — ${stop.point}` : ''}
                        </p>
                        {stop.items.map((item) => (
                          <p key={item.jobUid} className='body-01 text-gray-600'>
                            <Lozenge appearance={item.type === 'collection' ? 'inprogress' : 'success'}>
                              {item.type === 'collection' ? 'Collect' : 'Deliver'}
                            </Lozenge>{' '}
                            {item.requestType} — {item.requester}
                          </p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {!solving && preview && preview.problems.length > 0 && (
              <div className='mt-4 rounded border border-amber-300 bg-amber-50 p-3' data-testid='preview-problems'>
                <p className='body-02 font-semibold text-amber-900'>
                  {preview.problems.length} cannot be routed
                </p>
                <p className='body-01 mt-1 text-amber-800'>
                  These stay in the queue unless you fix the address or take them out.
                </p>

                {preview.problems.map((problem) => (
                  <div
                    key={problem.requestUid}
                    className='mt-3 border-t border-amber-200 pt-3'
                    data-testid='preview-problem'
                  >
                    <p className='body-02 font-semibold'>
                      {problem.requestType} — {problem.requester}
                    </p>
                    <p className='body-01 text-amber-900'>{problem.reason}</p>

                    {fixing === problem.requestUid ? (
                      <FixAddress
                        problem={problem}
                        onFixed={async () => {
                          setFixing(undefined);
                          await solve();
                        }}
                        onCancel={() => setFixing(undefined)}
                      />
                    ) : (
                      <div className='mt-2 flex gap-2'>
                        {problem.at !== 'details' && (
                          <SharedButton
                            type='button'
                            spacing='compact'
                            appearance='default'
                            label={problem.at === 'destination' ? 'Fix the destination' : 'Fix the collection'}
                            testId='fix-problem'
                            onClick={() => setFixing(problem.requestUid)}
                          />
                        )}
                        <SharedButton
                          type='button'
                          spacing='compact'
                          appearance='subtle'
                          label='Take out of the queue'
                          testId='drop-problem'
                          onClick={() => takeOut(problem.requestUid)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {step === 'assign' && (
          <>
            <p className='body-02'>
              Choose who takes this batch, or leave it for any driver to accept.
            </p>

            <div className='mt-4'>
              <DriverPicker
                drivers={drivers}
                loading={loadingDrivers}
                error={driversError}
                onRetry={loadDrivers}
                value={chosen}
                onChange={setChosen}
              />
            </div>
          </>
        )}
      </div>

      <div className='flex justify-end gap-2 border-t p-4'>
        {step === 'choose' && (
          <>
            <SharedButton type='button' appearance='subtle' label='Cancel' onClick={() => dialog?.closeDialog()} />
            {accepting < WORTH_SEQUENCING && (
              <SharedButton
                type='button'
                appearance='primary'
                label={`Commit ${decisions.length}`}
                testId='commit-plain'
                isLoading={busy}
                disabled={busy}
                onClick={() => commit(false)}
              />
            )}
          </>
        )}

        {step === 'preview' && (
          <>
            <SharedButton type='button' appearance='subtle' label='Back' onClick={() => setStep('choose')} />
            <SharedButton
              type='button'
              appearance='primary'
              // Says what will be in the run, and what will not, before anybody
              // goes looking for the two that are missing.
              label={
                preview
                  ? `Continue with ${preview.included.length}${
                      preview.problems.length ? ` (${preview.problems.length} remain in the queue)` : ''
                    }`
                  : 'Continue'
              }
              testId='to-assign'
              disabled={!preview || solving}
              onClick={() => setStep('assign')}
            />
          </>
        )}

        {step === 'assign' && (
          <>
            <SharedButton type='button' appearance='subtle' label='Back' onClick={() => setStep('preview')} />
            <SharedButton
              type='button'
              appearance='primary'
              label={chosen === ANY_DRIVER ? 'Create batch' : 'Create and assign batch'}
              testId='save-run'
              isLoading={busy}
              // The batch is created here, so it waits for a choice made on the
              // whole list rather than on however much of it had arrived.
              disabled={loadingDrivers}
              onClick={() => commit(true, drivers.find((driver) => driver.uid === chosen))}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default CommitDialog;
